// CommercialInvoicePreview.jsx - DOLLAR NATIVE VERSION
// All values are stored and displayed in USD - no exchange rate conversion needed
// UPDATED: Pallet calculation now EXACTLY matches PackingList logic
// UPDATED: Product table paginated at 12 data rows per page

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { getDoc, doc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import styles from './CommercialInvoicePreview.module.css';

// Icons
import {
    FiX,
    FiRefreshCw,
    FiFileText,
    FiPrinter,
    FiAlertCircle,
} from 'react-icons/fi';

// ============================================
// 1. HELPER FUNCTIONS
// ============================================

const ROWS_PER_PAGE = 12;

// Constants for pallet calculation (matching PackingList)
const MAX_HEIGHT_AIR = 160;
const MAX_HEIGHT_SEA = 220;

const formatNumber = (value, decimals = 3) => {
    if (!value && value !== 0) return '0';
    return Number(value).toFixed(decimals);
};

const formatWeight = (value) => {
    if (!value && value !== 0) return '0,000';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

const formatCurrencyUSD = (value) => {
    if (!value && value !== 0) return '$ -';
    return `$ ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatCurrentDateTime = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const numberToWords = (num) => {
    if (num === 0) return 'ZERO';

    const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
        'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
    const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

    const numToWordsHelper = (n) => {
        if (n < 20) return ones[n];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
        if (n < 1000) return ones[Math.floor(n / 100)] + ' HUNDRED' + (n % 100 ? ' ' + numToWordsHelper(n % 100) : '');
        if (n < 1000000) return numToWordsHelper(Math.floor(n / 1000)) + ' THOUSAND' + (n % 1000 ? ' ' + numToWordsHelper(n % 1000) : '');
        return numToWordsHelper(Math.floor(n / 1000000)) + ' MILLION' + (n % 1000000 ? ' ' + numToWordsHelper(n % 1000000) : '');
    };

    const dollars = Math.floor(num);
    const cents = Math.round((num - dollars) * 100);

    let result = numToWordsHelper(dollars) + ' DOLLARS';
    if (cents > 0) {
        result += ' AND ' + numToWordsHelper(cents) + ' CENTS';
    }
    return result;
};

const getCountryName = (countryCode) => {
    if (!countryCode) return '';

    const code = countryCode.trim().toUpperCase();

    if (code === 'COMMERCIAL' || code.includes('COMMERCIAL')) {
        return '';
    }

    const countryMap = {
        'AF': 'AFGHANISTAN', 'AL': 'ALBANIA', 'DZ': 'ALGERIA', 'AD': 'ANDORRA', 'AO': 'ANGOLA',
        'AG': 'ANTIGUA AND BARBUDA', 'AR': 'ARGENTINA', 'AM': 'ARMENIA', 'AU': 'AUSTRALIA', 'AT': 'AUSTRIA',
        'AZ': 'AZERBAIJAN', 'BS': 'BAHAMAS', 'BH': 'BAHRAIN', 'BD': 'BANGLADESH', 'BB': 'BARBADOS',
        'BY': 'BELARUS', 'BE': 'BELGIUM', 'BZ': 'BELIZE', 'BJ': 'BENIN', 'BT': 'BHUTAN',
        'BO': 'BOLIVIA', 'BA': 'BOSNIA AND HERZEGOVINA', 'BW': 'BOTSWANA', 'BR': 'BRAZIL', 'BN': 'BRUNEI',
        'BG': 'BULGARIA', 'BF': 'BURKINA FASO', 'BI': 'BURUNDI', 'KH': 'CAMBODIA', 'CM': 'CAMEROON',
        'CA': 'CANADA', 'CV': 'CAPE VERDE', 'CF': 'CENTRAL AFRICAN REPUBLIC', 'TD': 'CHAD', 'CL': 'CHILE',
        'CN': 'CHINA', 'CO': 'COLOMBIA', 'KM': 'COMOROS', 'CG': 'CONGO', 'CD': 'CONGO (DRC)',
        'CR': 'COSTA RICA', 'HR': 'CROATIA', 'CU': 'CUBA', 'CY': 'CYPRUS', 'CZ': 'CZECH REPUBLIC',
        'DK': 'DENMARK', 'DJ': 'DJIBOUTI', 'DM': 'DOMINICA', 'DO': 'DOMINICAN REPUBLIC', 'EC': 'ECUADOR',
        'EG': 'EGYPT', 'SV': 'EL SALVADOR', 'GQ': 'EQUATORIAL GUINEA', 'ER': 'ERITREA', 'EE': 'ESTONIA',
        'SZ': 'ESWATINI', 'ET': 'ETHIOPIA', 'FJ': 'FIJI', 'FI': 'FINLAND', 'FR': 'FRANCE',
        'GA': 'GABON', 'GM': 'GAMBIA', 'GE': 'GEORGIA', 'DE': 'GERMANY', 'GH': 'GHANA',
        'GR': 'GREECE', 'GD': 'GRENADA', 'GT': 'GUATEMALA', 'GN': 'GUINEA', 'GW': 'GUINEA-BISSAU',
        'GY': 'GUYANA', 'HT': 'HAITI', 'HN': 'HONDURAS', 'HU': 'HUNGARY', 'IS': 'ICELAND',
        'IN': 'INDIA', 'ID': 'INDONESIA', 'IR': 'IRAN', 'IQ': 'IRAQ', 'IE': 'IRELAND',
        'IL': 'ISRAEL', 'IT': 'ITALY', 'JM': 'JAMAICA', 'JP': 'JAPAN', 'JO': 'JORDAN',
        'KZ': 'KAZAKHSTAN', 'KE': 'KENYA', 'KI': 'KIRIBATI', 'KP': 'NORTH KOREA', 'KR': 'SOUTH KOREA',
        'KW': 'KUWAIT', 'KG': 'KYRGYZSTAN', 'LA': 'LAOS', 'LV': 'LATVIA', 'LB': 'LEBANON',
        'LS': 'LESOTHO', 'LR': 'LIBERIA', 'LY': 'LIBYA', 'LI': 'LIECHTENSTEIN', 'LT': 'LITHUANIA',
        'LU': 'LUXEMBOURG', 'MG': 'MADAGASCAR', 'MW': 'MALAWI', 'MY': 'MALAYSIA', 'MV': 'MALDIVES',
        'ML': 'MALI', 'MT': 'MALTA', 'MH': 'MARSHALL ISLANDS', 'MR': 'MAURITANIA', 'MU': 'MAURITIUS',
        'MX': 'MEXICO', 'FM': 'MICRONESIA', 'MD': 'MOLDOVA', 'MC': 'MONACO', 'MN': 'MONGOLIA',
        'ME': 'MONTENEGRO', 'MA': 'MOROCCO', 'MZ': 'MOZAMBIQUE', 'MM': 'MYANMAR', 'NA': 'NAMIBIA',
        'NR': 'NAURU', 'NP': 'NEPAL', 'NL': 'NETHERLANDS', 'NZ': 'NEW ZEALAND', 'NI': 'NICARAGUA',
        'NE': 'NIGER', 'NG': 'NIGERIA', 'NO': 'NORWAY', 'OM': 'OMAN', 'PK': 'PAKISTAN',
        'PW': 'PALAU', 'PA': 'PANAMA', 'PG': 'PAPUA NEW GUINEA', 'PY': 'PARAGUAY', 'PE': 'PERU',
        'PH': 'PHILIPPINES', 'PL': 'POLAND', 'PT': 'PORTUGAL', 'QA': 'QATAR', 'RO': 'ROMANIA',
        'RU': 'RUSSIA', 'RW': 'RWANDA', 'KN': 'SAINT KITTS AND NEVIS', 'LC': 'SAINT LUCIA',
        'VC': 'SAINT VINCENT AND THE GRENADINES', 'WS': 'SAMOA', 'SM': 'SAN MARINO', 'ST': 'SAO TOME AND PRINCIPE',
        'SA': 'SAUDI ARABIA', 'SN': 'SENEGAL', 'RS': 'SERBIA', 'SC': 'SEYCHELLES', 'SL': 'SIERRA LEONE',
        'SG': 'SINGAPORE', 'SK': 'SLOVAKIA', 'SI': 'SLOVENIA', 'SB': 'SOLOMON ISLANDS', 'SO': 'SOMALIA',
        'ZA': 'SOUTH AFRICA', 'SS': 'SOUTH SUDAN', 'ES': 'SPAIN', 'LK': 'SRI LANKA', 'SD': 'SUDAN',
        'SR': 'SURINAME', 'SE': 'SWEDEN', 'CH': 'SWITZERLAND', 'SY': 'SYRIA', 'TW': 'TAIWAN',
        'TJ': 'TAJIKISTAN', 'TZ': 'TANZANIA', 'TH': 'THAILAND', 'TL': 'TIMOR-LESTE', 'TG': 'TOGO',
        'TO': 'TONGA', 'TT': 'TRINIDAD AND TOBAGO', 'TN': 'TUNISIA', 'TR': 'TURKEY', 'TM': 'TURKMENISTAN',
        'TV': 'TUVALU', 'UG': 'UGANDA', 'UA': 'UKRAINE', 'AE': 'UNITED ARAB EMIRATES', 'GB': 'UNITED KINGDOM',
        'US': 'UNITED STATES', 'UY': 'URUGUAY', 'UZ': 'UZBEKISTAN', 'VU': 'VANUATU', 'VA': 'VATICAN CITY',
        'VE': 'VENEZUELA', 'VN': 'VIETNAM', 'YE': 'YEMEN', 'ZM': 'ZAMBIA', 'ZW': 'ZIMBABWE'
    };

    if (countryMap[code]) return countryMap[code];

    const stateCodes = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'EX'];
    if (stateCodes.includes(code)) return 'UNITED STATES';

    return countryCode;
};

// ============================================
// 2. PALLET CALCULATION FUNCTIONS (EXACTLY MATCHING PACKINGLIST)
// ============================================

const fetchAllPalletTypes = async () => {
    try {
        const palletRef = collection(db, 'parameters', 'types', 'tipo-palete');
        const snapshot = await getDocs(palletRef);
        const pallets = [];
        snapshot.forEach(docSnap => {
            pallets.push({ id: docSnap.id, ...docSnap.data() });
        });
        console.log(`📦 ${pallets.length} pallet types loaded`);
        return pallets;
    } catch (error) {
        console.error('Erro ao buscar tipos de palete:', error);
        return [];
    }
};

const fetchAllBoxTypes = async () => {
    try {
        const boxRef = collection(db, 'parameters', 'types', 'tipo-caixa');
        const snapshot = await getDocs(boxRef);
        const boxes = [];
        snapshot.forEach(docSnap => {
            boxes.push({ id: docSnap.id, ...docSnap.data() });
        });
        console.log(`📦 ${boxes.length} box types loaded`);
        return boxes;
    } catch (error) {
        console.error('Erro ao buscar tipos de caixa:', error);
        return [];
    }
};

const getPalletForBoxType = (boxTypeId, allPalletTypes, selectedPalletTypeId = null) => {
    if (!boxTypeId || !allPalletTypes || allPalletTypes.length === 0) {
        console.warn(`⚠️ No pallet types available or boxTypeId missing: ${boxTypeId}`);
        return null;
    }

    if (selectedPalletTypeId) {
        const selectedPallet = allPalletTypes.find(p => p.id === selectedPalletTypeId);
        if (selectedPallet) {
            const config = selectedPallet.boxConfigs?.find(c => c.boxTypeId === boxTypeId);
            if (config) {
                console.log(`✅ Found config for box ${boxTypeId} in selected pallet ${selectedPallet.name}`);
                return { pallet: selectedPallet, config };
            }
            console.warn(`⚠️ Box type ${boxTypeId} not configured in selected pallet ${selectedPallet.name}`);
        }
    }

    for (const pallet of allPalletTypes) {
        const config = pallet.boxConfigs?.find(c => c.boxTypeId === boxTypeId);
        if (config) {
            console.log(`✅ Found config for box ${boxTypeId} in pallet ${pallet.name}`);
            return { pallet, config };
        }
    }

    console.warn(`⚠️ No pallet configuration found for box type ${boxTypeId}`);
    return null;
};

const fetchProductDetailsForPacking = async (productId) => {
    if (!productId) return null;

    try {
        const productRef = doc(db, 'products', productId);
        const productSnap = await getDoc(productRef);

        if (!productSnap.exists()) {
            console.warn(`⚠️ Product ${productId} not found`);
            return null;
        }

        const data = productSnap.data();
        const technical = data.technical || {};

        let boxTypeId = data.boxTypeId ||
            technical.boxTypeId ||
            data.fiscal?.boxTypeId ||
            null;

        let unitsPerBox = 0;

        if (technical.boxTypeConfigs && Array.isArray(technical.boxTypeConfigs) && technical.boxTypeConfigs.length > 0) {
            const firstConfig = technical.boxTypeConfigs[0];
            if (firstConfig && firstConfig.unitsPerBox) {
                unitsPerBox = parseInt(firstConfig.unitsPerBox) || 0;
            }
            if (!boxTypeId && firstConfig) {
                boxTypeId = firstConfig.boxTypeId;
            }
        }

        if (unitsPerBox === 0 && data.unitsPerBox !== undefined && data.unitsPerBox !== null) {
            unitsPerBox = parseInt(data.unitsPerBox) || 0;
        }

        if (unitsPerBox === 0 && technical.unitsPerBox !== undefined && technical.unitsPerBox !== null) {
            unitsPerBox = parseInt(technical.unitsPerBox) || 0;
        }

        if (unitsPerBox === 0 && data.fiscal?.unitsPerBox !== undefined && data.fiscal?.unitsPerBox !== null) {
            unitsPerBox = parseInt(data.fiscal.unitsPerBox) || 0;
        }

        const gramaturaUnit = technical.gramaturaUnit || data.gramaturaUnit || 'g';
        const unitLower = String(gramaturaUnit).toLowerCase();

        let netWeightInKg = 0;
        if (technical.gramatura !== undefined && technical.gramatura !== null && parseFloat(technical.gramatura) > 0) {
            const rawGram = parseFloat(technical.gramatura) || 0;
            netWeightInKg = (unitLower === 'g' || unitLower === 'ml') ? rawGram / 1000 : rawGram;
        } else if (data.netWeight !== undefined && data.netWeight !== null) {
            netWeightInKg = parseFloat(data.netWeight) || 0;
        }

        let grossWeightInKg = 0;
        if (technical.grossWeight !== undefined && technical.grossWeight !== null && parseFloat(technical.grossWeight) > 0) {
            const rawGross = parseFloat(technical.grossWeight) || 0;
            grossWeightInKg = (unitLower === 'g' || unitLower === 'ml') ? rawGross / 1000 : rawGross;
        } else if (data.grossWeight !== undefined && data.grossWeight !== null && parseFloat(data.grossWeight) > 0) {
            grossWeightInKg = parseFloat(data.grossWeight) || 0;
        }

        let boxHeight = 0, boxWidth = 0, boxLength = 0;
        if (technical.boxHeight !== undefined && technical.boxHeight !== null) boxHeight = parseFloat(technical.boxHeight) || 0;
        if (technical.boxWidth !== undefined && technical.boxWidth !== null) boxWidth = parseFloat(technical.boxWidth) || 0;
        if (technical.boxLength !== undefined && technical.boxLength !== null) boxLength = parseFloat(technical.boxLength) || 0;
        if (boxHeight === 0 && data.boxHeight !== undefined && data.boxHeight !== null) boxHeight = parseFloat(data.boxHeight) || 0;
        if (boxWidth === 0 && data.boxWidth !== undefined && data.boxWidth !== null) boxWidth = parseFloat(data.boxWidth) || 0;
        if (boxLength === 0 && data.boxLength !== undefined && data.boxLength !== null) boxLength = parseFloat(data.boxLength) || 0;

        let ncmCode = '';
        if (data.fiscal?.ncmId) {
            try {
                const ncmRef = doc(db, 'parameters/data/ncm', data.fiscal.ncmId);
                const ncmSnap = await getDoc(ncmRef);
                if (ncmSnap.exists()) {
                    ncmCode = ncmSnap.data().ncmCode || '';
                }
            } catch (e) {
                console.warn('Erro ao buscar NCM:', e);
            }
        }

        return {
            id: productId,
            unitsPerBox,
            boxTypeId,
            gramaturaUnit,
            netWeightInKg,
            grossWeightInKg,
            boxHeight,
            boxWidth,
            boxLength,
            code: data.code || '',
            commercialName: data.commercialName || '',
            name: data.name || '',
            ncm: ncmCode,
            technical
        };

    } catch (error) {
        console.error('Erro ao buscar produto:', error);
        return null;
    }
};

const calculatePalletsMatchingPackingList = async (orderItems, shippingMethod, totalBoxes, order) => {
    if (totalBoxes <= 0) {
        return { totalPallets: 0, pallets: [], boxesPerPalletUsed: 0 };
    }

    try {
        const allPalletTypes = await fetchAllPalletTypes();
        const allBoxTypes = await fetchAllBoxTypes();

        const boxTypeMap = new Map();
        allBoxTypes.forEach(box => boxTypeMap.set(box.id, box));

        const effectivePalletTypeId = order?.palletTypeId || null;
        const isAir = shippingMethod === 'air';

        let totalBoxesSum = 0;
        let totalPalletsSum = 0;
        let firstBoxHeight = 30;
        let firstPalletWidth = 100;
        let firstPalletDepth = 120;
        let firstPalletBaseHeight = 15;
        let firstBoxesPerLayer = 1;
        let firstBoxesPerPallet = 0;

        for (const item of orderItems) {
            const productData = await fetchProductDetailsForPacking(item.productId);
            const quantity = parseFloat(item.quantity) || 0;

            if (!productData || quantity <= 0) continue;

            const boxTypeId = productData.boxTypeId;
            const boxHeight = productData.boxHeight || 30;
            const unitsPerBox = productData.unitsPerBox || 0;

            let itemBoxes = 0;
            if (unitsPerBox > 0 && quantity > 0) {
                itemBoxes = quantity / unitsPerBox;
            } else if (quantity > 0) {
                itemBoxes = quantity;
            }

            if (itemBoxes <= 0) continue;

            let boxesPerPallet = 0;
            let boxesPerLayer = 1;

            if (boxTypeId && allPalletTypes.length > 0) {
                const result = getPalletForBoxType(boxTypeId, allPalletTypes, effectivePalletTypeId);

                if (result) {
                    const { pallet, config } = result;

                    boxesPerPallet = isAir
                        ? parseFloat(config?.boxesPerPalletAir) || 0
                        : parseFloat(config?.boxesPerPalletSea) || 0;
                    boxesPerLayer = isAir
                        ? parseFloat(config?.boxesPerLayerAir) || 0
                        : parseFloat(config?.boxesPerLayerSea) || 0;

                    if (firstBoxesPerPallet === 0 && boxesPerPallet > 0) {
                        firstBoxesPerPallet = boxesPerPallet;
                        firstBoxesPerLayer = boxesPerLayer > 0 ? boxesPerLayer : 1;
                        firstPalletWidth = parseFloat(pallet.palletWidth) || 100;
                        firstPalletDepth = parseFloat(pallet.palletDepth) || 120;
                        firstPalletBaseHeight = parseFloat(pallet.palletBaseHeight) || parseFloat(pallet.palletHeight) || 15;
                        firstBoxHeight = boxHeight > 0 ? boxHeight : 30;
                    }
                }
            }

            if (boxesPerPallet === 0) {
                boxesPerPallet = isAir ? 36 : 45;
                boxesPerLayer = 4;
                if (firstBoxesPerPallet === 0) {
                    firstBoxesPerPallet = boxesPerPallet;
                    firstBoxesPerLayer = boxesPerLayer;
                    firstBoxHeight = boxHeight > 0 ? boxHeight : 30;
                }
            }

            const productPallets = itemBoxes / boxesPerPallet;

            totalBoxesSum += itemBoxes;
            totalPalletsSum += productPallets;

            console.log(`📦 Product ${item.code}: boxes=${itemBoxes}, boxesPerPallet=${boxesPerPallet}, pallets=${productPallets}`);
        }

        let totalPhysicalPallets = Math.ceil(totalPalletsSum);

        if (totalPhysicalPallets === 0 && totalBoxesSum > 0) {
            totalPhysicalPallets = 1;
        }

        const effectiveBoxesPerPallet = firstBoxesPerPallet > 0 ? firstBoxesPerPallet : (isAir ? 36 : 45);

        const maxHeight = isAir ? MAX_HEIGHT_AIR : MAX_HEIGHT_SEA;
        const pallets = [];

        if (totalPhysicalPallets > 0) {
            const fullPallets = Math.floor(totalPalletsSum);
            const hasPartial = totalPhysicalPallets > fullPallets;

            const partialFraction = totalPalletsSum - fullPallets;
            const remainingBoxes = Math.ceil(effectiveBoxesPerPallet * partialFraction);

            for (let i = 0; i < fullPallets; i++) {
                const layers = Math.ceil(effectiveBoxesPerPallet / firstBoxesPerLayer);
                const finalHeight = Math.min(firstPalletBaseHeight + (firstBoxHeight * layers), maxHeight);

                pallets.push({
                    width: firstPalletWidth,
                    depth: firstPalletDepth,
                    finalHeight: finalHeight,
                    isPartial: false,
                    boxesOnPallet: effectiveBoxesPerPallet,
                    palletIndex: i + 1
                });
            }

            if (hasPartial && remainingBoxes > 0) {
                const layers = Math.ceil(remainingBoxes / firstBoxesPerLayer);
                const finalHeight = Math.min(firstPalletBaseHeight + (firstBoxHeight * layers), maxHeight);

                pallets.push({
                    width: firstPalletWidth,
                    depth: firstPalletDepth,
                    finalHeight: finalHeight,
                    isPartial: true,
                    boxesOnPallet: remainingBoxes,
                    palletIndex: fullPallets + 1
                });
            }

            if (fullPallets === 0 && hasPartial && pallets.length === 0) {
                const layers = Math.ceil(effectiveBoxesPerPallet * partialFraction / firstBoxesPerLayer);
                const finalHeight = Math.min(firstPalletBaseHeight + (firstBoxHeight * layers), maxHeight);

                pallets.push({
                    width: firstPalletWidth,
                    depth: firstPalletDepth,
                    finalHeight: finalHeight,
                    isPartial: true,
                    boxesOnPallet: Math.ceil(effectiveBoxesPerPallet * partialFraction),
                    palletIndex: 1
                });
            }

            if (pallets.length === 0 && totalPhysicalPallets > 0) {
                const layers = Math.ceil(totalBoxesSum / firstBoxesPerLayer);
                const finalHeight = Math.min(firstPalletBaseHeight + (firstBoxHeight * layers), maxHeight);

                pallets.push({
                    width: firstPalletWidth,
                    depth: firstPalletDepth,
                    finalHeight: finalHeight,
                    isPartial: totalPhysicalPallets > 1,
                    boxesOnPallet: Math.ceil(totalBoxesSum / totalPhysicalPallets),
                    palletIndex: 1
                });
            }
        }

        console.log('✅ Pallet calculation complete (matching PackingList):', {
            totalBoxes: totalBoxesSum,
            totalPalletsSum,
            totalPhysicalPallets,
            effectiveBoxesPerPallet,
            palletCount: pallets.length
        });

        return {
            totalPallets: totalPhysicalPallets,
            pallets,
            boxesPerPalletUsed: effectiveBoxesPerPallet
        };

    } catch (error) {
        console.error('Error in pallet calculation:', error);

        const fallbackBoxesPerPallet = shippingMethod === 'air' ? 36 : 45;
        const totalPhysicalPallets = Math.ceil(totalBoxes / fallbackBoxesPerPallet);

        return {
            totalPallets: totalPhysicalPallets,
            pallets: [],
            boxesPerPalletUsed: fallbackBoxesPerPallet
        };
    }
};

// ============================================
// 3. DATA FETCHING FUNCTIONS
// ============================================

const fetchLiveClientData = async (clientId) => {
    if (!clientId) {
        console.warn('⚠️ No clientId provided to fetchLiveClientData');
        return null;
    }

    try {
        console.log('🔍 Fetching LIVE client data for ID:', clientId);

        const clientRef = doc(db, 'clients', clientId);
        const clientSnap = await getDoc(clientRef);

        if (!clientSnap.exists()) {
            console.warn('⚠️ Client document not found for ID:', clientId);
            return null;
        }

        const clientData = clientSnap.data();
        console.log('✅ Live client data fetched successfully:', {
            id: clientId,
            razaoSocial: clientData.razaoSocial,
            nomeFantasia: clientData.nomeFantasia,
            addressesCount: clientData.addresses?.length || 0,
        });

        return clientData;
    } catch (error) {
        console.error('❌ Error fetching live client data:', error);
        return null;
    }
};

const extractContactsFromAddresses = (addresses, isImporter = false) => {
    const contacts = { email: '', phone: '', name: '' };

    if (!addresses || !Array.isArray(addresses)) return contacts;

    const filteredAddresses = addresses.filter(addr => addr.isImporter === isImporter);

    for (const addr of filteredAddresses) {
        if (addr.contacts && Array.isArray(addr.contacts) && addr.contacts.length > 0) {
            for (const contact of addr.contacts) {
                if (!contacts.email && contact.email) {
                    contacts.email = contact.email;
                }
                if (!contacts.phone && contact.telefone) {
                    contacts.phone = contact.telefone;
                }
                if (!contacts.name && contact.nome) {
                    contacts.name = contact.nome;
                }
            }
            if (contacts.email && contacts.phone && contacts.name) break;
        }
    }

    return contacts;
};

const fetchProductDetailsForInvoice = async (productId) => {
    if (!productId) return null;
    try {
        const productRef = doc(db, 'products', productId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
            const data = productSnap.data();
            const technical = data.technical || {};

            let unitsPerBox = 0;
            let boxTypeId = data.boxTypeId || technical.boxTypeId || data.fiscal?.boxTypeId || null;

            if (technical.boxTypeConfigs && Array.isArray(technical.boxTypeConfigs) && technical.boxTypeConfigs.length > 0) {
                const firstConfig = technical.boxTypeConfigs[0];
                if (firstConfig && firstConfig.unitsPerBox) {
                    unitsPerBox = parseFloat(firstConfig.unitsPerBox) || 0;
                }
                if (!boxTypeId && firstConfig) {
                    boxTypeId = firstConfig.boxTypeId;
                }
            } else if (technical.unitsPerBox !== undefined && technical.unitsPerBox !== null) {
                unitsPerBox = parseFloat(technical.unitsPerBox) || 0;
            }

            if (unitsPerBox === 0 && data.unitsPerBox !== undefined && data.unitsPerBox !== null) {
                unitsPerBox = parseFloat(data.unitsPerBox) || 0;
            }

            const gramaturaUnit = technical.gramaturaUnit || data.gramaturaUnit || 'g';
            const unitLower = String(gramaturaUnit).toLowerCase();

            let netWeightInKg = 0;
            if (technical.gramatura !== undefined && technical.gramatura !== null && parseFloat(technical.gramatura) > 0) {
                const rawGram = parseFloat(technical.gramatura) || 0;
                netWeightInKg = (unitLower === 'g' || unitLower === 'ml') ? rawGram / 1000 : rawGram;
            } else if (data.netWeight !== undefined && data.netWeight !== null) {
                netWeightInKg = parseFloat(data.netWeight) || 0;
            }

            let ncmCode = '';
            if (data.fiscal?.ncmId) {
                try {
                    const ncmRef = doc(db, 'parameters/data/ncm', data.fiscal.ncmId);
                    const ncmSnap = await getDoc(ncmRef);
                    if (ncmSnap.exists()) {
                        ncmCode = ncmSnap.data().ncmCode || '';
                    }
                } catch (e) {
                    console.warn('Erro ao buscar NCM:', e);
                }
            }

            return {
                id: productId,
                unitsPerBox,
                boxTypeId,
                gramaturaUnit,
                netWeightInKg,
                code: data.code || '',
                commercialName: data.commercialName || data.name || '',
                ncm: ncmCode,
                technical
            };
        }
    } catch (error) {
        console.error('Erro ao buscar produto:', error);
    }
    return null;
};

const fetchStockInfoForInvoice = async (productId) => {
    try {
        if (!productId) return null;
        const stockRef = collection(db, 'current-stock');
        const q = query(stockRef, where('rawMaterialId', '==', productId), where('quantity', '>', 0.001));
        const snapshot = await getDocs(q);
        if (snapshot.empty) return null;

        let stockItems = [];
        snapshot.forEach(docSnap => {
            stockItems.push({ id: docSnap.id, ...docSnap.data() });
        });

        stockItems.sort((a, b) => {
            const dateA = a.expirationDate?.toDate?.() || new Date(a.expirationDate || 0);
            const dateB = b.expirationDate?.toDate?.() || new Date(b.expirationDate || 0);
            return dateA - dateB;
        });

        const selectedStock = stockItems[0];
        if (!selectedStock) return null;

        const formatDateMMYYYY = (dateValue) => {
            if (!dateValue) return '';
            try {
                let date;
                if (dateValue.toDate) date = dateValue.toDate();
                else if (typeof dateValue === 'string') date = new Date(dateValue);
                else if (dateValue instanceof Date) date = dateValue;
                else return '';
                if (isNaN(date.getTime())) return '';
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${month}/${year}`;
            } catch { return ''; }
        };

        return {
            lotNumber: selectedStock.lotNumber || '',
            manufacturerDateFormatted: formatDateMMYYYY(selectedStock.manufacturerDate || selectedStock.manufactureDate),
            expirationDateFormatted: formatDateMMYYYY(selectedStock.expirationDate),
            quantityAvailable: selectedStock.quantity || 0,
            location: selectedStock.locationDescription || ''
        };
    } catch (error) {
        console.error('Erro ao buscar informações de estoque:', error);
        return null;
    }
};

const determineShippingMethod = (order) => {
    if (order.shippingMethod) {
        const method = order.shippingMethod.toLowerCase();
        if (method === 'air' || method === 'aereo' || method === 'airfreight') return 'air';
        if (method === 'sea' || method === 'maritimo' || method === 'maritime') return 'sea';
    }
    if (order.transportMode) {
        const mode = order.transportMode.toLowerCase();
        if (mode === 'air' || mode === 'aereo') return 'air';
        if (mode === 'sea' || mode === 'maritimo') return 'sea';
    }
    return 'sea';
};

// ============================================
// 4. MAIN COMPONENT - DOLLAR NATIVE
// ============================================

const CommercialInvoicePreview = ({
    order,
    onClose,
    onDownload,
    canGenerate = true,
    addToast,
    logoPath = '/assets/grupo_logo.png',
    shipperData = null,
    clientCountry = ''
}) => {
    const [loading, setLoading] = useState(true);
    const [invoiceData, setInvoiceData] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const printRef = useRef(null);

    const selectedIncoterm = order?.incoterm || 'EXW';

    const allItems = useMemo(() => {
        if (!invoiceData?.items || invoiceData.items.length === 0) return [];
        return invoiceData.items;
    }, [invoiceData?.items]);

    // Split items into chunks of ROWS_PER_PAGE (12)
    const itemChunks = useMemo(() => {
        if (!allItems.length) return [];
        const chunks = [];
        for (let i = 0; i < allItems.length; i += ROWS_PER_PAGE) {
            chunks.push(allItems.slice(i, i + ROWS_PER_PAGE));
        }
        return chunks;
    }, [allItems]);

    const totalPages = itemChunks.length || 1;

    const loadInvoiceData = useCallback(async () => {
        setLoading(true);
        try {
            if (!order || !order.items || order.items.length === 0) {
                setInvoiceData(null);
                setLoading(false);
                return;
            }

            let liveOrder = { ...order };
            if (order.id) {
                try {
                    const orderRef = doc(db, 'sales-orders', order.id);
                    const orderSnap = await getDoc(orderRef);
                    if (orderSnap.exists()) {
                        liveOrder = { ...liveOrder, ...orderSnap.data() };
                    }
                } catch (e) {
                    console.warn('Erro ao buscar dados atualizados do pedido:', e);
                }
            }

            const usdValues = liveOrder.usdValues || liveOrder.calculatedValues || {};
            const itemsTotalUSD = parseFloat(usdValues.itemsTotal) || 0;
            const discountAmountUSD = parseFloat(usdValues.discountAmount) || 0;
            const ticketDiscountUSD = parseFloat(usdValues.ticketDiscountAmount) || 0;
            const subtotalAfterDiscountsUSD = parseFloat(usdValues.subtotalWithDiscounts) || 0;
            const totalUSD = parseFloat(usdValues.total) || 0;

            console.log('📊 USD Values from order:', {
                itemsTotalUSD,
                discountAmountUSD,
                ticketDiscountUSD,
                subtotalAfterDiscountsUSD,
                totalUSD
            });

            const shippingMethod = determineShippingMethod(liveOrder);
            const countryName = getCountryName(clientCountry || liveOrder.destination || '');
            const incoterm = liveOrder.incoterm || 'EXW';

            let liveClientData = null;
            if (liveOrder.clientId) {
                liveClientData = await fetchLiveClientData(liveOrder.clientId);
            }

            let clientContacts = { email: '', phone: '', name: '' };

            if (liveClientData && liveClientData.addresses) {
                const contactsFromLive = extractContactsFromAddresses(liveClientData.addresses, false);
                clientContacts = {
                    email: contactsFromLive.email || '',
                    phone: contactsFromLive.phone || '',
                    name: contactsFromLive.name || liveClientData.razaoSocial || liveClientData.nomeFantasia || ''
                };
            }

            if (!clientContacts.email || !clientContacts.phone) {
                const addresses = liveOrder.addresses || [];
                const contactsFromOrder = extractContactsFromAddresses(addresses, false);
                if (!clientContacts.email) clientContacts.email = contactsFromOrder.email || '';
                if (!clientContacts.phone) clientContacts.phone = contactsFromOrder.phone || '';
                if (!clientContacts.name) clientContacts.name = contactsFromOrder.name || '';
            }

            if (!clientContacts.email) clientContacts.email = liveOrder.userEmail || liveOrder.contactEmail || '';
            if (!clientContacts.phone) clientContacts.phone = liveOrder.contactPhone || '';
            if (!clientContacts.name) clientContacts.name = liveOrder.clientName || '';

            let importerContacts = {
                email: liveOrder.importerEmail || '',
                phone: liveOrder.importerPhone || '',
                name: liveOrder.importerContact || '',
                companyName: liveOrder.importerCompanyName || '',
                address: liveOrder.importerAddress || ''
            };

            if (liveClientData && liveClientData.addresses) {
                const contactsFromLiveImporter = extractContactsFromAddresses(liveClientData.addresses, true);
                if (!importerContacts.email) importerContacts.email = contactsFromLiveImporter.email || '';
                if (!importerContacts.phone) importerContacts.phone = contactsFromLiveImporter.phone || '';
                if (!importerContacts.name) importerContacts.name = contactsFromLiveImporter.name || '';

                const importerAddresses = liveClientData.addresses.filter(addr => addr.isImporter === true);
                for (const addr of importerAddresses) {
                    if (!importerContacts.companyName && addr.importerCompanyName) {
                        importerContacts.companyName = addr.importerCompanyName;
                    }
                    if (!importerContacts.address) {
                        const addrStr = `${addr.logradouro || ''} ${addr.numero || ''} ${addr.complemento || ''} ${addr.bairro || ''} ${addr.cidade || ''} ${addr.uf || ''} ${addr.cep || ''}`.trim();
                        if (addrStr) importerContacts.address = addrStr;
                    }
                }
            }

            if (!importerContacts.email || !importerContacts.phone) {
                const addresses = liveOrder.addresses || [];
                const contactsFromOrderImporter = extractContactsFromAddresses(addresses, true);
                if (!importerContacts.email) importerContacts.email = contactsFromOrderImporter.email || '';
                if (!importerContacts.phone) importerContacts.phone = contactsFromOrderImporter.phone || '';
                if (!importerContacts.name) importerContacts.name = contactsFromOrderImporter.name || '';
            }

            if (!importerContacts.email && clientContacts.email) importerContacts.email = clientContacts.email;
            if (!importerContacts.phone && clientContacts.phone) importerContacts.phone = clientContacts.phone;
            if (!importerContacts.name && clientContacts.name) importerContacts.name = clientContacts.name;

            let clientName = liveOrder.clientName || '';
            if (liveClientData) {
                clientName = liveClientData.razaoSocial ||
                    liveClientData.nomeFantasia ||
                    liveClientData.nome ||
                    liveOrder.clientName ||
                    '';
            }

            let deliveryAddress = liveOrder.deliveryAddress || liveOrder.destination || '';
            if (liveClientData && liveClientData.addresses) {
                const regularAddresses = liveClientData.addresses.filter(addr => addr.isImporter !== true);
                if (regularAddresses.length > 0) {
                    const primaryAddr = regularAddresses[0];
                    const addrStr = `${primaryAddr.logradouro || ''} ${primaryAddr.numero || ''} ${primaryAddr.complemento || ''} ${primaryAddr.bairro || ''} ${primaryAddr.cidade || ''} ${primaryAddr.uf || ''} ${primaryAddr.cep || ''}`.trim();
                    if (addrStr) deliveryAddress = addrStr;
                }
            }

            const hasImporter = liveOrder.useImporter === true ||
                (importerContacts.companyName && importerContacts.companyName.trim() !== '') ||
                (importerContacts.address && importerContacts.address.trim() !== '');

            let effectiveShipperData = shipperData;
            if (!effectiveShipperData) {
                try {
                    const settingsRef = doc(db, 'parameters', 'global-settings');
                    const settingsSnap = await getDoc(settingsRef);
                    if (settingsSnap.exists()) {
                        const settings = settingsSnap.data();
                        effectiveShipperData = {
                            name: settings.shipperName || 'GRUPO B2E INDUSTRIA E COMERCIO DE COSMETICOS LTDA',
                            taxId: settings.shipperTaxId || '22.013.644/0001-70',
                            address: settings.shipperAddress || 'AVENIDA JOAO PAULO ABLAS NO. 1400, GALPAO NO. 4 COTIA, SP, CEP: 06711-250, BRAZIL',
                            countryOfExport: settings.countryOfExport || 'BRAZIL',
                            countryOfManufacture: settings.countryOfManufacture || 'BRAZIL'
                        };
                    }
                } catch (e) {
                    console.warn('Erro ao buscar shipper data:', e);
                }
            }

            if (!effectiveShipperData) {
                effectiveShipperData = {
                    name: 'GRUPO B2E INDUSTRIA E COMERCIO DE COSMETICOS LTDA',
                    taxId: '22.013.644/0001-70',
                    address: 'AVENIDA JOAO PAULO ABLAS NO. 1400, GALPAO NO. 4 COTIA, SP, CEP: 06711-250, BRAZIL',
                    countryOfExport: 'BRAZIL',
                    countryOfManufacture: 'BRAZIL',
                    email: 'export@brazil2export.com',
                };
            }

            const processedItems = [];
            let totalQuantity = 0;
            let totalNetWeight = 0;
            let totalItemsValueUSD = 0;
            let totalBoxes = 0;

            for (const item of liveOrder.items) {
                const quantity = parseFloat(item.quantity) || 0;
                const unitPriceUSD = parseFloat(item.unitPrice) || 0;
                const itemTotalUSD = quantity * unitPriceUSD;

                const productDetails = await fetchProductDetailsForInvoice(item.productId);
                const stockInfo = await fetchStockInfoForInvoice(item.productId);

                const productName = productDetails?.commercialName || item.name || '';
                const ncm = productDetails?.ncm || '';

                let unitNetWeight = 0;
                if (productDetails && productDetails.netWeightInKg > 0) {
                    unitNetWeight = productDetails.netWeightInKg;
                } else if (item.netWeight !== undefined && item.netWeight !== null) {
                    unitNetWeight = parseFloat(item.netWeight) || 0;
                }

                const netWeight = quantity * unitNetWeight;

                let unitsPerBox = productDetails?.unitsPerBox || 0;
                if (unitsPerBox === 0 && item.unitsPerBox !== undefined && item.unitsPerBox !== null) {
                    unitsPerBox = parseFloat(item.unitsPerBox) || 0;
                }

                let boxes = 0;
                if (unitsPerBox > 0 && quantity > 0) {
                    boxes = quantity / unitsPerBox;
                } else if (quantity > 0) {
                    boxes = quantity;
                }

                totalQuantity += quantity;
                totalNetWeight += netWeight;
                totalItemsValueUSD += itemTotalUSD;
                totalBoxes += boxes;

                const lotText = stockInfo?.lotNumber ? `LOTE: ${stockInfo.lotNumber}` : 'LOTE: N/A';
                const manufacturerText = stockInfo?.manufacturerDateFormatted ? `F:${stockInfo.manufacturerDateFormatted}` : 'F:__/____';
                const expirationText = stockInfo?.expirationDateFormatted ? `V:${stockInfo.expirationDateFormatted}` : 'V:__/____';
                const ncmText = ncm ? `NCM: ${ncm}` : 'NCM: _______';

                const fullDescription = `${productName}\n${lotText}\n${manufacturerText}. ${expirationText}\n${ncmText}`;

                processedItems.push({
                    ...item,
                    productName,
                    ncm,
                    lotNumber: stockInfo?.lotNumber || '',
                    manufacturerDateFormatted: stockInfo?.manufacturerDateFormatted || '',
                    expirationDateFormatted: stockInfo?.expirationDateFormatted || '',
                    quantity,
                    unitNetWeight,
                    netWeight,
                    unitsPerBox,
                    boxes,
                    unitValueUSD: unitPriceUSD,
                    totalUSD: itemTotalUSD,
                    fullDescription,
                    isBonus: item.isBonus || false,
                    isSample: item.isSample || false
                });
            }

            let totalPhysicalPallets = 0;
            let physicalPallets = [];
            let boxesPerPalletUsed = 0;

            try {
                console.log('🔄 Calculating pallets using PackingList logic...');
                console.log(`📦 Total boxes: ${totalBoxes}, Shipping method: ${shippingMethod}`);

                const palletResult = await calculatePalletsMatchingPackingList(
                    liveOrder.items,
                    shippingMethod,
                    totalBoxes,
                    liveOrder
                );

                totalPhysicalPallets = palletResult.totalPallets;
                physicalPallets = palletResult.pallets || [];
                boxesPerPalletUsed = palletResult.boxesPerPalletUsed || 0;

                console.log('✅ Pallets calculated using PackingList logic:', {
                    totalPhysicalPallets,
                    physicalPalletsCount: physicalPallets.length,
                    boxesPerPalletUsed
                });
            } catch (error) {
                console.error('Error calculating pallets:', error);
                const fallbackBoxesPerPallet = shippingMethod === 'air' ? 36 : 45;
                if (totalBoxes > 0 && fallbackBoxesPerPallet > 0) {
                    totalPhysicalPallets = Math.ceil(totalBoxes / fallbackBoxesPerPallet);
                } else if (totalBoxes > 0) {
                    totalPhysicalPallets = 1;
                }
            }

            const advancePaymentUSD = parseFloat(liveOrder.advancePaymentUSD) || 0;
            const balanceUSD = totalUSD - advancePaymentUSD;
            const totalValueWords = numberToWords(totalUSD);

            const discountInfo = {
                clientDiscountPercent: parseFloat(liveOrder.clientDiscountPercent) || 0,
                discountAmountUSD: discountAmountUSD,
                ticketDiscountUSD: ticketDiscountUSD,
                subtotalAfterDiscountsUSD: subtotalAfterDiscountsUSD,
                itemsTotalUSD: itemsTotalUSD,
                totalUSD: totalUSD,
                hasDiscounts: discountAmountUSD > 0 || ticketDiscountUSD > 0
            };

            setInvoiceData({
                items: processedItems,
                totals: {
                    quantity: totalQuantity,
                    netWeight: totalNetWeight,
                    valueUSD: totalUSD,
                    valueWords: totalValueWords,
                    boxes: Math.ceil(totalBoxes),
                    pallets: totalPhysicalPallets,
                    advancePaymentUSD: advancePaymentUSD,
                    balanceUSD: balanceUSD,
                    balanceWords: numberToWords(balanceUSD),
                    itemsTotalUSD: itemsTotalUSD,
                    discountAmountUSD: discountAmountUSD,
                    ticketDiscountUSD: ticketDiscountUSD,
                    subtotalAfterDiscountsUSD: subtotalAfterDiscountsUSD,
                },
                discountInfo: discountInfo,
                shipper: effectiveShipperData,
                clientContacts,
                importerContacts,
                hasImporter,
                countryName,
                shippingMethod: shippingMethod === 'air' ? 'AIR' : 'SEA',
                incoterm: incoterm,
                orderCode: liveOrder.code || '',
                clientName: clientName,
                deliveryAddress: deliveryAddress,
                createdAt: liveOrder.createdAt || new Date(),
                liveClientData: liveClientData,
                physicalPallets: physicalPallets,
                boxesPerPalletUsed: boxesPerPalletUsed
            });

        } catch (error) {
            console.error('Error loading invoice data:', error);
            if (addToast) addToast('Erro ao carregar dados da fatura comercial.', 'Error');
        } finally {
            setLoading(false);
        }
    }, [order, shipperData, clientCountry, addToast]);

    useEffect(() => {
        if (order) loadInvoiceData();
    }, [order, loadInvoiceData]);

    const handleDownload = async () => {
        if (!canGenerate) {
            if (addToast) addToast('Você não tem permissão para gerar este documento.', 'Warning');
            return;
        }
        setDownloading(true);
        try {
            if (onDownload) await onDownload();
        } finally {
            setDownloading(false);
        }
    };

    const handlePrint = () => {
        const printContent = printRef.current;
        if (!printContent) return;

        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        if (!printWindow) {
            if (typeof addToast === 'function') {
                addToast('Por favor, permita pop-ups para imprimir.', 'Warning');
            } else {
                alert('Por favor, permita pop-ups para imprimir.');
            }
            return;
        }

        const clone = printContent.cloneNode(true);
        const noPrintElements = clone.querySelectorAll('.no-print');
        noPrintElements.forEach(el => el.remove());
        clone.style.maxHeight = 'none';
        clone.style.height = 'auto';
        clone.style.overflow = 'visible';
        clone.style.background = '#ffffff';
        clone.style.boxShadow = 'none';
        clone.style.margin = '0';
        clone.style.padding = '0';

        const orderCode = order?.code || order?.id || 'INV';

        printWindow.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Commercial Invoice - ${orderCode}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 8mm 12mm 15mm 12mm;
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    html, body {
      background: #ffffff !important;
      font-family: Calibri, Arial, sans-serif;
      font-size: 9.5px;
      color: #000000;
      width: 100%;
    }
    .print-document {
      width: 100%;
      margin: 0 auto;
      padding: 0 !important;
      background: #ffffff !important;
    }
    .no-print { display: none !important; }
    .no-page-break {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    .header-section {
      page-break-after: avoid !important;
      break-after: avoid !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }

    /* Each printed page = one block that must fit within one A4 sheet */
    .page-block {
      page-break-after: always !important;
      break-after: page !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      display: block !important;
      width: 100% !important;
    }
    .page-block:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }

    /* Table rules: no split inside a page-block because we paginate manually */
    .product-table-wrapper {
      overflow: visible !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    table {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    thead {
      display: table-header-group;
    }
    tr {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    td, th {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }

    .signatures-section {
      width: 100%;
      background: #ffffff !important;
      padding-top: 10px;
      padding-bottom: 5px;
      border-top: 1px solid #ccc;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      position: fixed;
      bottom: 0 !important;
    }
    .signature {
      width: 100%;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      margin-top: 10px;
      position: fixed;
      bottom: 0 !important;
    }
    .payment-summary {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
  </style>
</head>
<body>
  <div class="print-document">
    ${clone.innerHTML}
  </div>
  <script>
    window.onload = function() {
      window.print();
      setTimeout(function() {
        window.close();
      }, 500);
    };
  <\/script>
</body>
</html>`);
        printWindow.document.close();
    };

    if (!order) return null;

    const hasInvoiceData = invoiceData && invoiceData.items && invoiceData.items.length > 0;
    const displayIncoterm = invoiceData?.incoterm || selectedIncoterm || 'EXW';
    const today = new Date();
    const day = String(today.getUTCDate()).padStart(2, '0');
    const month = String(today.getUTCMonth() + 1).padStart(2, '0');
    const year = today.getUTCFullYear();
    const formattedDate = `${day}/${month}/${year}`;

    // ============================================================
    // RENDER HELPERS
    // ============================================================

    const renderTableHead = () => (
        <thead>
            <tr style={{ background: '#e8eaf6' }}>
                <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '35px' }}>#</th>
                <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>PRODUCT DESCRIPTION</th>
                <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '55px' }}>QUANTITY</th>
                <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '55px' }}>UNIT NET<br />WEIGHT (Kg)</th>
                <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '45px' }}>UNIT OF MEASURE</th>
                <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '60px' }}>NET WEIGHT<br />(Kgs)</th>
                <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '55px' }}>UNIT VALUE<br />{displayIncoterm} (USD)</th>
                <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '65px' }}>TOTAL VALUE<br />(USD)</th>
            </tr>
        </thead>
    );

    const renderItemRow = (item, globalIdx) => (
        <tr key={globalIdx} style={item.isBonus || item.isSample ? { background: '#fff3e0' } : {}}>
            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{globalIdx + 1}</td>
            <td style={{ border: '1px solid #ddd', padding: '6px', whiteSpace: 'pre-wrap', fontSize: '9px', lineHeight: '1.3' }}>
                <div>{item.fullDescription || item.productName || item.name}</div>
                <div style={{ fontSize: '8px', color: '#666' }}>
                    {item.isBonus && <span style={{ background: '#ff9800', color: '#fff', padding: '1px 4px', borderRadius: '2px', marginRight: '4px' }}>BÔNUS</span>}
                    {item.isSample && <span style={{ background: '#2196f3', color: '#fff', padding: '1px 4px', borderRadius: '2px', marginRight: '4px' }}>AMOSTRA</span>}
                </div>
            </td>
            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatNumber(item.quantity, 0)}</td>
            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatNumber(item.unitNetWeight, 3)}</td>
            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>UNITS</td>
            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatWeight(item.netWeight)}</td>
            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatNumber(item.unitValueUSD, 3)}</td>
            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold' }}>
                {formatCurrencyUSD(item.totalUSD)}
            </td>
        </tr>
    );

    const renderTotalsRows = () => (
        <>
            {/* SUBTOTAL ROW */}
            <tr style={{ fontWeight: 'bold', background: '#f5f5f5' }}>
                <td colSpan="2" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>SUBTOTAL</td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatNumber(invoiceData.totals.quantity, 0)}</td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}></td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}></td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatWeight(invoiceData.totals.netWeight)}</td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}></td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                    {formatCurrencyUSD(invoiceData.totals.itemsTotalUSD)}
                </td>
            </tr>

            {/* DISCOUNT ROWS - Only show if there are discounts */}
            {invoiceData.discountInfo?.hasDiscounts && (
                <>
                    {invoiceData.discountInfo.discountAmountUSD > 0 && (
                        <tr style={{ background: '#fff3e0' }}>
                            <td colSpan="6" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                                <span style={{ color: '#e65100' }}>CLIENT DISCOUNTS ({invoiceData.discountInfo.clientDiscountPercent}%)</span>
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}></td>
                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', color: '#e65100' }}>
                                - {formatCurrencyUSD(invoiceData.discountInfo.discountAmountUSD)}
                            </td>
                        </tr>
                    )}
                    {invoiceData.discountInfo.ticketDiscountUSD > 0 && (
                        <tr style={{ background: '#fff3e0' }}>
                            <td colSpan="6" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                                <span style={{ color: '#e65100' }}>Desconto do Boleto</span>
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}></td>
                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', color: '#e65100' }}>
                                - {formatCurrencyUSD(invoiceData.discountInfo.ticketDiscountUSD)}
                            </td>
                        </tr>
                    )}
                    {/* SUBTOTAL AFTER DISCOUNTS */}
                    <tr style={{ fontWeight: 'bold', background: '#e8f5e9' }}>
                        <td colSpan="6" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                            SUBTOTAL AFTER DISCOUNTS
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}></td>
                        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                            {formatCurrencyUSD(invoiceData.totals.subtotalAfterDiscountsUSD)}
                        </td>
                    </tr>
                </>
            )}

            {/* TOTAL ROW */}
            <tr style={{ fontWeight: 'bold', background: '#e8eaf6' }}>
                <td colSpan="6" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right', fontSize: '11px' }}>
                    TOTAL
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}></td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '11px' }}>
                    {formatCurrencyUSD(invoiceData.totals.valueUSD)}
                </td>
            </tr>
        </>
    );

    // ============================================================
    // PAGE RENDERERS
    // ============================================================

    // Page 1: full header + shipper block + first chunk of items
    const renderFirstPage = () => {
        const chunk = itemChunks[0] || [];
        return (
            <div className="page-block" style={{ marginBottom: '0' }}>
                {/* HEADER */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '2px solid #1a237e',
                    paddingBottom: '12px',
                    marginBottom: '12px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{
                            width: '200px',
                            height: '70px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            overflow: 'hidden',
                            background: '#fff',
                            padding: '6px'
                        }}>
                            <img
                                src={logoPath}
                                alt="Logo"
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<span style="font-size: 12px; color: #666; font-weight: bold;">LOGO</span>';
                                }}
                            />
                        </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1a237e', letterSpacing: '2px' }}>
                            COMMERCIAL INVOICE
                        </div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#333', marginTop: '4px' }}>
                            Ref: {invoiceData.orderCode}
                        </div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#333', marginTop: '4px' }}>
                            DATE: {formattedDate}
                        </div>
                    </div>
                    <div style={{ textAlign: 'left' }}>
                        <div>
                            <div style={{ fontSize: '9px', color: '#666' }}>PAG.</div>
                            <div style={{ fontSize: '9px', color: '#666' }}>FQ-38</div>
                            <div style={{ fontSize: '9px', color: '#666' }}>REVISION: 01</div>
                        </div>
                    </div>
                </div>

                {/* SHIPPER, CONSIGNEE & IMPORTER */}
                <div className="header-section" style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '15px',
                    marginBottom: '12px',
                    border: '1px solid #ddd',
                    padding: '12px',
                    background: '#fafafa'
                }}>
                    <div>
                        <div style={{ fontWeight: 'bold', fontSize: '8px', color: '#666' }}>1. SHIPPER - EXPORTER:</div>
                        <div style={{ fontSize: '8px', fontWeight: 'bold' }}>{invoiceData.shipper?.name || 'N/A'}</div>
                        <div style={{ fontSize: '8px', whiteSpace: 'pre-wrap' }}>{(invoiceData.shipper?.address || '').replace(/\n/g, '<br />')}</div>
                        <div style={{ fontSize: '8px' }}>CNPJ: {invoiceData.shipper?.taxId || 'N/A'}</div>
                        <div style={{ fontSize: '8px' }}>PHONE: +55(11) 3637-7711</div>
                        <div style={{ fontSize: '8px' }}>EMAIL: export@brazil2export.com</div>
                    </div>
                    <div>
                        <div style={{ fontWeight: 'bold', fontSize: '8px', color: '#666' }}>2. CONSIGNEE:</div>
                        <div style={{ fontSize: '8px', fontWeight: 'bold' }}>{invoiceData.clientName || 'N/A'}</div>
                        <div style={{ fontSize: '8px', whiteSpace: 'pre-wrap' }}>{(invoiceData.deliveryAddress || '').replace(/\n/g, '<br />')}</div>
                        <div style={{ fontSize: '8px' }}>EMAIL: {invoiceData.clientContacts?.email || 'N/A'}</div>
                        <div style={{ fontSize: '8px' }}>PHONE: {invoiceData.clientContacts?.phone || 'N/A'}</div>
                    </div>
                    <div>
                        <div style={{ fontWeight: 'bold', fontSize: '8px', color: '#666' }}>3. IMPORTER:</div>
                        <div style={{ fontSize: '8px', fontWeight: 'bold' }}>
                            {invoiceData.hasImporter
                                ? (invoiceData.importerContacts?.companyName || invoiceData.clientName)
                                : invoiceData.clientName}
                        </div>
                        <div style={{ fontSize: '8px', whiteSpace: 'pre-wrap' }}>
                            {invoiceData.hasImporter
                                ? (invoiceData.importerContacts?.address || invoiceData.deliveryAddress || '').replace(/\n/g, '<br />')
                                : (invoiceData.deliveryAddress || '').replace(/\n/g, '<br />')}
                        </div>
                        <div style={{ fontSize: '8px' }}>EMAIL: {invoiceData.importerContacts?.email || invoiceData.clientContacts?.email || 'N/A'}</div>
                        <div style={{ fontSize: '8px' }}>PHONE: {invoiceData.importerContacts?.phone || invoiceData.clientContacts?.phone || 'N/A'}</div>
                        {invoiceData.hasImporter && invoiceData.importerContacts?.name && (
                            <div style={{ fontSize: '8px' }}>CONTACT: {invoiceData.importerContacts.name}</div>
                        )}
                    </div>
                </div>

                {/* ADDITIONAL INFO ROW */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    gap: '8px',
                    marginBottom: '12px',
                    padding: '8px 12px',
                    background: '#f8f9fa',
                    border: '1px solid #eee',
                    fontSize: '8px'
                }}>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>4. Country of Export:</span>
                        <span> {invoiceData.shipper?.countryOfExport || 'BRAZIL'}</span>
                    </div>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>5. Manufacturer:</span>
                        <span> {invoiceData.shipper?.name || 'N/A'}</span>
                    </div>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>6. Country of Manufacture:</span>
                        <span> {invoiceData.shipper?.countryOfManufacture || 'BRAZIL'}</span>
                    </div>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>7. Country of Destination:</span>
                        <span> {invoiceData.countryName || order.destination || 'N/A'}</span>
                    </div>
                </div>

                {/* TABLE - first chunk */}
                <div className="product-table-wrapper" style={{ marginBottom: '12px', overflow: 'visible' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', border: '1px solid #ddd' }}>
                        {renderTableHead()}
                        <tbody>
                            {chunk.map((item, idx) => renderItemRow(item, idx))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Middle pages: item rows only (12 per page)
    const renderMiddlePage = (chunk, pageIndex) => (
        <div className="page-block" key={`middle-${pageIndex}`} style={{ marginBottom: '0' }}>
            <div className="product-table-wrapper" style={{ marginBottom: '12px', overflow: 'visible' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', border: '1px solid #ddd' }}>
                    {renderTableHead()}
                    <tbody>
                        {chunk.map((item, idx) => renderItemRow(item, pageIndex * ROWS_PER_PAGE + idx))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    // Last page: final chunk of items (if any) + totals + packages + payment + footer
    const renderLastPage = () => {
        const lastChunkIndex = totalPages - 1;
        const lastChunk = itemChunks[lastChunkIndex] || [];
        const isFirstAndLast = totalPages === 1;

        return (
            <div className="page-block" style={{ marginBottom: '0' }}>
                {/* If this is NOT the first page, the header info (1..7) doesn't repeat.
                    Only the last chunk of items + totals are shown. */}
                {!isFirstAndLast && (
                    <div className="product-table-wrapper" style={{ marginBottom: '12px', overflow: 'visible' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', border: '1px solid #ddd' }}>
                            {renderTableHead()}
                            <tbody>
                                {lastChunk.map((item, idx) =>
                                    renderItemRow(item, lastChunkIndex * ROWS_PER_PAGE + idx)
                                )}
                                {renderTotalsRows()}
                            </tbody>
                        </table>
                    </div>
                )}

                {isFirstAndLast && (
                    <div className="product-table-wrapper" style={{ marginBottom: '12px', overflow: 'visible' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', border: '1px solid #ddd' }}>
                            {renderTableHead()}
                            <tbody>
                                {lastChunk.map((item, idx) => renderItemRow(item, idx))}
                                {renderTotalsRows()}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* PACKAGES INFO */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    gap: '8px',
                    marginBottom: '8px',
                    padding: '8px 12px',
                    background: '#f8f9fa',
                    border: '1px solid #eee',
                    fontSize: '8px'
                }}>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>8A. No. of Pallets:</span>
                        <span> {invoiceData.totals.pallets || 0}</span>
                        {invoiceData.boxesPerPalletUsed > 0 && (
                            <span style={{ fontSize: '7px', color: '#666', display: 'block' }}>
                                ({invoiceData.boxesPerPalletUsed} cx/palete)
                            </span>
                        )}
                    </div>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>8B. Total No. of Packages:</span>
                        <span> {Math.ceil(invoiceData.totals.boxes)} BOXES</span>
                    </div>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>9. Product Description:</span>
                        <br /><span> HAIR CARE COSMETIC PRODUCTS</span>
                    </div>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>10. Incoterm:</span>
                        <span> {displayIncoterm}</span>
                    </div>
                    <div>
                        <span style={{ fontWeight: 'bold' }}>11. Total No. of units / Total Net Weight:</span>
                        <br />
                        <span style={{ marginLeft: '25%' }}>{formatNumber(invoiceData.totals.quantity, 0)} / {formatWeight(invoiceData.totals.netWeight)}</span>
                    </div>
                </div>

                {/* PAYMENT SUMMARY */}
                <div className="payment-summary" style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px',
                    marginTop: '8px',
                    marginBottom: '12px',
                    padding: '12px 16px',
                    background: '#f0f4ff',
                    border: '1px solid #1a237e',
                    borderRadius: '4px'
                }}>
                    <div>
                        <div style={{ fontSize: '9px', fontWeight: 'bold', color: '#1a237e' }}>PAYMENT TERMS</div>
                        <div style={{ fontSize: '8px', marginTop: '4px', lineHeight: '1.6' }}>
                            <div>
                                <strong>Total Invoice Value ({displayIncoterm}):</strong> {formatCurrencyUSD(invoiceData.totals.valueUSD)}
                            </div>
                            <div><strong>Advance Payment Received:</strong> {formatCurrencyUSD(invoiceData.totals.advancePaymentUSD)}</div>
                            <div><strong>Balance to be Paid:</strong> {formatCurrencyUSD(invoiceData.totals.balanceUSD)}</div>
                        </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '8px', color: '#666' }}>
                        <div><strong>Shipping Method:</strong> {invoiceData.shippingMethod}</div>
                        <div style={{ fontSize: '7px', marginTop: '4px', color: '#1a237e', fontWeight: 'bold' }}>
                            Total Amount ({displayIncoterm}): {invoiceData.totals.valueWords}
                        </div>
                        {invoiceData.totals.balanceUSD > 0 && (
                            <div style={{ fontSize: '7px', marginTop: '2px', color: '#333' }}>
                                Balance: {invoiceData.totals.balanceWords}
                            </div>
                        )}
                    </div>
                </div>

                {/* FOOTER WRAPPER (unchanged) */}
                <div className="footer-wrapper" style={{
                    flexShrink: '0',
                    width: '100%'
                }}>
                    <div className="signatures-section" style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '20px',
                        paddingBottom: '5px',
                        borderTop: '1px solid #ccc',
                        marginTop: '10px'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '8px' }}>Elaborado por: Nisrine El Hazzat</div>
                            <div style={{ fontSize: '9px', color: '#666', marginTop: '1px', borderTop: '1px solid #ccc', paddingTop: '4px' }}>
                                Data: 24 / 08 / 2026
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '8px' }}>Revisado por: Magali Murro</div>
                            <div style={{ fontSize: '9px', color: '#666', marginTop: '1px', borderTop: '1px solid #ccc', paddingTop: '4px' }}>
                                Data: 24 / 08 / 2026
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '8px' }}>Aprovado por: Nisrine El Hazzat</div>
                            <div style={{ fontSize: '9px', color: '#666', marginTop: '1px', borderTop: '1px solid #ccc', paddingTop: '4px' }}>
                                24 / 08 / 2026
                            </div>
                        </div>
                    </div>
                    <span className='signature' style={{
                        marginTop: '10px',
                        textAlign: 'right',
                        fontSize: '8px',
                        color: '#666',
                        fontStyle: 'italic'
                    }}>
                        Documento gerado eletronicamente em: {formatCurrentDateTime()}
                    </span>
                </div>
            </div>
        );
    };

    const renderInvoice = () => {
        const pages = [];
        // Page 1
        pages.push(<React.Fragment key="page-1">{renderFirstPage()}</React.Fragment>);
        // Middle pages (all chunks except the last one, which goes on the last page)
        for (let i = 1; i < totalPages - 1; i++) {
            pages.push(
                <React.Fragment key={`page-mid-${i}`}>
                    {renderMiddlePage(itemChunks[i], i)}
                </React.Fragment>
            );
        }
        // Last page
        pages.push(<React.Fragment key={`page-last-${totalPages}`}>{renderLastPage()}</React.Fragment>);
        return pages;
    };

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div
                className={`${styles.formContainer} ${styles.formContainerLarge}`}
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: '1200px' }}
            >
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FiFileText size={24} style={{ marginRight: '10px', color: '#1a237e' }} /> Commercial Invoice</h2>
                        <p className={styles.modalSubtitle}>
                            Pedido: <strong>{order.code}</strong> - {invoiceData?.clientName || order.clientName}
                            {invoiceData?.boxesPerPalletUsed > 0 && (
                                <span style={{ marginLeft: '12px', background: '#1976d2', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', color: 'white' }}>
                                    {invoiceData.boxesPerPalletUsed} cx/palete
                                </span>
                            )}
                            {invoiceData?.totals?.pallets > 0 && (
                                <span style={{ marginLeft: '12px', background: '#4caf50', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', color: 'white' }}>
                                    {invoiceData.totals.pallets} paletes
                                </span>
                            )}
                            {hasInvoiceData && (
                                <span style={{ marginLeft: '12px', background: '#1a237e', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', color: 'white' }}>
                                    {totalPages} página{totalPages > 1 ? 's' : ''}
                                </span>
                            )}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className={styles.modalCloseButton} disabled={downloading} aria-label="Fechar">
                        <FiX size={24} />
                    </button>
                </div>

                <div
                    ref={printRef}
                    className={styles.formContent}
                    style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto', background: '#f5f5f5' }}
                >
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '60px', background: '#fff', borderRadius: '8px' }}>
                            <FiRefreshCw size={40} className={styles.spinner} />
                            <p style={{ marginTop: '20px', color: '#666', fontSize: '16px' }}>Carregando dados da fatura comercial...</p>
                        </div>
                    ) : !hasInvoiceData ? (
                        <div style={{ textAlign: 'center', padding: '60px', background: '#fff', borderRadius: '8px', color: '#666' }}>
                            <FiAlertCircle size={40} style={{ color: '#ff9800' }} />
                            <p style={{ marginTop: '20px', fontSize: '16px' }}>Não foi possível carregar os dados da fatura comercial.</p>
                        </div>
                    ) : (
                        <div style={{ background: '#fff', padding: '25px', borderRadius: '8px', fontFamily: 'Calibri, Arial, sans-serif' }}>
                            {renderInvoice()}
                        </div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <button type="button" onClick={onClose} className={styles.secondaryButton} disabled={downloading}>
                        Fechar
                    </button>
                    <button
                        type="button"
                        onClick={handlePrint}
                        className={styles.printButton}
                        disabled={loading || !hasInvoiceData}
                        title="Imprimir Commercial Invoice"
                        style={{
                            padding: '8px 20px',
                            borderRadius: '4px',
                            border: 'none',
                            background: '#1a237e',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px'
                        }}
                    >
                        <FiPrinter size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CommercialInvoicePreview;